import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search,
  Visibility,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { parseDate } from '../utils/dateUtils';
import { voucherService } from '../services/api';
import { Voucher, VoucherStatus } from '../types/voucher';

interface PartnerVouchersProps {
  partnerId: string;
  partnerType: 'MERCHANT' | 'RETAILER' | 'AGENT';
}

const PartnerVouchers: React.FC<PartnerVouchersProps> = ({ partnerId, partnerType }) => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    fetchVouchers();
  }, [partnerId, page, rowsPerPage, searchTerm]);

  const fetchVouchers = async () => {
    if (!loading) setRefreshing(true);
    try {
      const params: any = {
        page,
        size: rowsPerPage,
      };

      // Filter based on partner type
      if (partnerType === 'MERCHANT') {
        params.merchantId = partnerId;
      } else if (partnerType === 'RETAILER' || partnerType === 'AGENT') {
        params.retailerId = partnerId;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await voucherService.searchVouchers(params);
      setVouchers(response.content);
      setTotalElements(response.totalElements);
    } catch (error) {
      console.error('Failed to fetch vouchers:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchVouchers();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: VoucherStatus): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case VoucherStatus.AWAITING_PAYMENT:
        return 'warning';
      case VoucherStatus.CUSTOMER_PAID:
        return 'info';
      case VoucherStatus.AWAITING_RETAILER_SETTLEMENT:
        return 'secondary';
      case VoucherStatus.AWAITING_MERCHANT_SETTLEMENT:
        return 'secondary';
      case VoucherStatus.SETTLED:
        return 'success';
      case VoucherStatus.EXPIRED:
        return 'default';
      case VoucherStatus.REFUNDED:
        return 'error';
      case VoucherStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPartnerTypeLabel = () => {
    switch (partnerType) {
      case 'MERCHANT':
        return 'Created Vouchers';
      case 'RETAILER':
        return 'Facilitated Vouchers';
      case 'AGENT':
        return 'Facilitated Vouchers';
      default:
        return 'Vouchers';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{getPartnerTypeLabel()}</Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search vouchers..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {vouchers.length === 0 ? (
        <Alert severity="info">
          No vouchers found for this partner.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Voucher Number</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  {partnerType === 'MERCHANT' && <TableCell>Retailer</TableCell>}
                  {(partnerType === 'RETAILER' || partnerType === 'AGENT') && <TableCell>Merchant</TableCell>}
                  <TableCell>Created Date</TableCell>
                  <TableCell>Expires At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vouchers.map((voucher) => (
                  <TableRow key={voucher.id} hover>
                    <TableCell>{voucher.voucherNumber}</TableCell>
                    <TableCell>{voucher.productDescription}</TableCell>
                    <TableCell>
                      {voucher.currency} {voucher.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={voucher.voucherStatusDescription || voucher.voucherStatus}
                        color={getStatusColor(voucher.voucherStatus)}
                        size="small"
                      />
                    </TableCell>
                    {partnerType === 'MERCHANT' && (
                      <TableCell>
                        {voucher.retailerId || '-'}
                      </TableCell>
                    )}
                    {(partnerType === 'RETAILER' || partnerType === 'AGENT') && (
                      <TableCell>
                        {voucher.merchantName || voucher.merchantId}
                      </TableCell>
                    )}
                    <TableCell>
                      {voucher.dateCreated
                        ? format(parseDate(voucher.dateCreated), 'PPp')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {format(parseDate(voucher.expiresAt), 'PPp')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/voucher/${voucher.voucherNumber}`)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
};

export default PartnerVouchers;
